import {
  ENTITY_LABELS,
  PRIORITY_KEYS,
  PRIORITY_MAP,
  RICERCA_PAGE_SIZE,
  RULLO_PRIORITIES,
  isPeriodo,
  type PeriodoKey,
} from "@/lib/constants";
import { canonicaCategoria } from "@/lib/categorie";
import { fmtDispatchId } from "@/lib/format";
import { articleHref } from "@/lib/slug";
import type { StrapiCase } from "@/lib/nd-types";
import type { EntityKind, NewsItem, Priority, TipoDerivato } from "@/lib/types";

/**
 * Il modello della pagina «Rullo informativo»
 * (app/rullo, e con la sua `VistaRullo` anche di /ricerca e della scheda
 * paese): lo stato che sta nell'indirizzo, come si
 * riscrive in un link, e la riga di dispaccio nella forma che la pagina
 * rende.
 *
 * Sta in `lib/` e senza direttiva perché lo leggono sia il Server
 * Component della rotta sia i componenti client di components/rullo: una
 * funzione esportata da un modulo «use client» e letta dal server è il
 * riferimento al modulo, non la funzione (vedi LEAD_DISPACCI nei
 * learnings). Per la stessa ragione qui non si importa `lib/nd-client`:
 * la preparazione dei dati condivisa con /ricerca sta in lib/ricerca.ts.
 */

/** Dove vive la pagina. Una costante sola: la leggono i link, il
 *  modulo di ricerca senza JavaScript, la voce RULLO della barra e il
 *  piede. */
export const RULLO_PATH = "/rullo";

/** Quanti dispacci per pagina sulla lista della scheda paese, nella
 *  veste di prima: i cento che aveva /ricerca. */
export const RULLO_PAGINA = RICERCA_PAGE_SIZE;

/** Quanti dispacci per pagina nella veste del registro (/rullo e
 *  /ricerca): quindici, come il
 *  riferimento approvato del 02/10/2026. La card dei servizi che si
 *  inserisce dopo la quinta riga non conta: le pubblicazioni restano
 *  quindici. */
export const REGISTRO_PAGINA = 15;

/** La lista in cui si sta leggendo. Tre rotte rendono lo stesso elenco
 *  con gli stessi componenti (components/rullo) e cambiano solo qui:
 *
 *  - /rullo, il rullo vero: i tipi del rullo, archivio storico escluso;
 *  - /ricerca, tutto l'archivio, storico compreso, e in più l'intervallo
 *    chiuso `?from=`/`?before=` con cui /archivio apre un anno;
 *  - /paesi/[slug], la scheda paese: il paese è la pagina e non un
 *    filtro, quindi niente menu dei Paesi.
 *
 *  Fino al 30/09/2026 /ricerca e la scheda paese avevano una barra loro
 *  (<BarraRicerca>, monospazio, coi glifi) e un elenco loro
 *  (<DispatchList>): la stessa lista in due stili a un clic di distanza.
 *
 *  Viaggia dentro lo stato, e non a fianco, perché ogni link della pagina
 *  (filtri, metadati delle righe, riferimenti, paginazione) passa da
 *  `hrefRullo(stato, …)`: così nessuno può dimenticarsi su che rotta sta.
 *  Solo dati semplici, perché attraversa il confine server→client. */
export type VistaRullo = {
  /** L'indirizzo della lista, senza query. */
  percorso: string;
  /** I tipi che il menu offre. La scelta piena si scrive come nessuna
   *  scelta (vedi `normalizzaTipi`). */
  tipi: Priority[];
  /** Tipi che si possono scegliere solo esplicitamente e che la scelta
   *  piena non comprende. Sul Rullo è l'archivio storico: di base la
   *  lista sono le pubblicazioni correnti, e lo storico (101.818 dispacci
   *  fermi al 2023) si apre dal menu dei tipi, sulla stessa pagina. */
  tipiEspliciti?: Priority[];
  /** Il riepilogo del menu quando la scelta è piena. */
  tutti: string;
  /** Il paese della pagina, sulla scheda paese. `strategia` è come a
   *  monte si trovano i suoi dispacci: per relation (`tag`) o, se il
   *  paese non ne ha, per nome nel testo (`search`). */
  paese?: { nome: string; slug: string; strategia: "tag" | "search" };
  /** Se la lista legge `?from=`/`?before=`. Solo /ricerca: ci atterrano
   *  i link per anno di /archivio. */
  intervallo?: boolean;
  /** Se l'ultima pagina della lista senza filtri è la fine del rullo, e
   *  va detto. Solo /rullo: su /ricerca il confine cade a metà. */
  fineDelRullo?: boolean;
  /** Le parole con cui la veste del registro (components/rullo/registro)
   *  nomina la lista: l'etichetta del campo di ricerca e quella della
   *  fascia di chiusura. Senza, sono quelle del Rullo. */
  etichettaCampo?: string;
  etichettaFascia?: string;
  /** La veste «lettura» del Rullo (consegna del 06/10/2026,
   *  uploads/Consegna_Rullo_OI.zip): una barra sola, la riga di stato con
   *  data, conteggio e Sintesi / Lettura, nessun inserto fra i dispacci.
   *  Solo /rullo: /ricerca resta nella veste del registro. Accende anche
   *  la lettura di `?vista=` (vedi `ModoLettura`). */
  veste?: "lettura";
};

export const VISTA_RULLO: VistaRullo = {
  percorso: RULLO_PATH,
  tipi: RULLO_PRIORITIES,
  tipiEspliciti: ["archivio"],
  tutti: "Tutti i tipi del rullo",
  fineDelRullo: true,
  veste: "lettura",
};

/** Dal 21/10/2026 la ricerca vive su /rullo (app/rullo): il nome resta per
 *  chi legge `VISTA_RICERCA`, /ricerca rimanda qui. */
export const RICERCA_PATH = "/rullo";

export const VISTA_RICERCA: VistaRullo = {
  percorso: RICERCA_PATH,
  tipi: PRIORITY_KEYS,
  tutti: "Tutti i tipi",
  intervallo: true,
  etichettaCampo: "CERCA NELL’ARCHIVIO",
  etichettaFascia: "L’ARCHIVIO",
};

/** La scheda di un paese. Tutti i tipi, archivio compreso: è la
 *  profondità del paese, non il suo rullo. */
export function vistaPaese(nome: string, slug: string, strategia: "tag" | "search"): VistaRullo {
  return {
    percorso: `/paesi/${slug}`,
    tipi: PRIORITY_KEYS,
    tutti: "Tutti i tipi",
    paese: { nome, slug, strategia },
  };
}

/** Come si leggono le righe nella veste «lettura»: Lettura con titolo e
 *  sommario (il valore della prova approvata, quindi quello di base, che
 *  non si scrive nell'indirizzo), Sintesi coi soli titoli
 *  (`?vista=sintesi`). Fuori da quella veste è sempre «lettura» e non
 *  si scrive mai. */
export type ModoLettura = "lettura" | "sintesi";

/** I tipi derivati che il menu dei tipi del Rullo sa filtrare (`?tipo=`),
 *  nell'ordine del menu: le notizie, i casi e i report, con la regola della
 *  redazione (`TIPO_DERIVATO` di lib/caso.ts). «Notizia» è dal 25/10/2026
 *  (richiesta del committente: dalla pagina di un Paese si arriva al rullo e
 *  lì si devono poter scegliere notizie, casi e report): tutto quello che
 *  non sta in un caso o in un report, cioè la stessa parola che le righe
 *  scrivono accanto al titolo. Non è il tipo a monte «Notizie»
 *  (`?priority=notizie`, 217 dispacci), che con questo non c'entra. */
export const FILONI = ["notizia", "caso", "report"] as const;
export type Filone = (typeof FILONI)[number];
export function isFilone(v: string): v is Filone {
  return (FILONI as readonly string[]).includes(v);
}

/** Le parole con cui il Rullo nomina quello che elenca, secondo il tipo
 *  scelto (`?tipo=`): con «Notizie» la testata dell'elenco dice «Tutte le
 *  notizie», l'esito «134 notizie nella selezione», e così via (25/10/2026,
 *  richiesta del committente: «invece di tutti i dispacci, tutte le notizie,
 *  oppure tutti i report»). Senza tipo restano i dispacci. */
export const PAROLE_TIPO: Record<Filone | "", { tutti: string; uno: string; molti: string; nessuno: string }> = {
  "": { tutti: "Tutti i dispacci", uno: "dispaccio", molti: "dispacci", nessuno: "Nessun dispaccio" },
  notizia: { tutti: "Tutte le notizie", uno: "notizia", molti: "notizie", nessuno: "Nessuna notizia" },
  caso: { tutti: "Tutti i casi", uno: "caso", molti: "casi", nessuno: "Nessun caso" },
  report: { tutti: "Tutti i report", uno: "report", molti: "report", nessuno: "Nessun report" },
};

/** Tutto quello che la pagina legge dall'indirizzo, già validato. */
export type StatoRullo = {
  vista: VistaRullo;
  modo: ModoLettura;
  q: string;
  /** I tipi scelti, nell'ordine di RULLO_PRIORITIES. Vuoto = tutti i
   *  tipi del rullo: la selezione piena si scrive come nessuna
   *  selezione, così la stessa vista ha un indirizzo solo. */
  tipi: Priority[];
  /** `?tipo=caso` / `?tipo=report`: i soli dispacci dei filoni che sono
   *  casi (da 3 a 8 voci di cronologia, `soloCasi` in lib/report-dati.ts)
   *  o report (da 9 in su, `agenzieDeiReport`, gli stessi di /report). Non è un
   *  `type` a monte, quindi non sta in `tipi` né in `PRIORITY_KEYS`: è un
   *  restringimento in più, che si somma agli altri. Vuoto = nessuno. Lo
   *  stesso nome di `?tipo=` della pagina paese (lib/paese-stato.ts). */
  filone: Filone | "";
  /** Il paese, filtrato sul titolo della relation `countries`. */
  paese: string;
  cat: string;
  date: PeriodoKey | "";
  /** Un'entità della voce `wiki` (persone, aziende, città…), filtrata
   *  sul titolo. `etype` serve solo all'etichetta del chip. */
  etype: string;
  entity: string;
  /** `?from=` e `?before=`, come sono scritti nell'indirizzo e solo se
   *  sono date valide. Vuoti fuori da /ricerca. */
  da: string;
  fino: string;
  page: number;
};

type SP = { [key: string]: string | string[] | undefined };

function first(v: string | string[] | undefined): string {
  return (Array.isArray(v) ? v[0] : v)?.trim() ?? "";
}

/** I tipi, ripuliti: solo quelli del rullo, nel loro ordine, e la
 *  selezione piena ridotta a vuota. L'archivio storico non è un tipo
 *  del rullo e un `?priority=archivio` scritto a mano cade qui: la
 *  pagina resta sul rullo intero invece di uscire vuota. */
export function normalizzaTipi(
  tipi: Iterable<string>,
  universo: Priority[] = RULLO_PRIORITIES,
  espliciti: Priority[] = []
): Priority[] {
  const chiesti = new Set(tipi);
  const scelti = universo.filter((p) => chiesti.has(p));
  const extra = espliciti.filter((p) => chiesti.has(p) && !universo.includes(p));
  if (!extra.length && scelti.length === universo.length) return [];
  return [...scelti, ...extra];
}

/** Se la lista è dentro l'archivio storico: un tipo scelto solo
 *  esplicitamente (vedi `tipiEspliciti`). Serve a dirlo in pagina e a
 *  contare le categorie sul perimetro giusto. */
export function inStorico(s: Pick<StatoRullo, "tipi" | "vista">): boolean {
  const espliciti = s.vista.tipiEspliciti ?? [];
  return s.tipi.some((t) => espliciti.includes(t));
}

/** Se la lista è tutta archivio storico, su qualunque vista: sul Rullo
 *  lo storico è un tipo esplicito (`inStorico`), su /ricerca è un tipo
 *  come gli altri, e la lista è storica quando è l'unico scelto. Serve
 *  alla veste del registro per dirlo in pagina. */
export function soloStorico(s: Pick<StatoRullo, "tipi" | "vista">): boolean {
  return inStorico(s) || (s.tipi.length > 0 && s.tipi.every((t) => t === "archivio"));
}

/** Una data dell'indirizzo, tenuta com'è scritta se è valida. Una data
 *  storta tornerebbe indietro come filtro assurdo e la lista uscirebbe
 *  vuota senza dire perché. */
function data(v: string): string {
  return v && !Number.isNaN(Date.parse(v)) ? v : "";
}

/** Legge l'indirizzo. */
export function leggiStato(sp: SP, vista: VistaRullo = VISTA_RULLO): StatoRullo {
  const d = first(sp.date);
  const n = parseInt(first(sp.page) || "1", 10);
  return {
    vista,
    modo: vista.veste === "lettura" && first(sp.vista) === "sintesi" ? "sintesi" : "lettura",
    q: first(sp.q),
    tipi: normalizzaTipi(
      first(sp.priority).split(",").map((p) => p.trim()),
      vista.tipi,
      vista.tipiEspliciti
    ),
    filone: isFilone(first(sp.tipo)) ? (first(sp.tipo) as Filone) : "",
    /* Sulla scheda paese il paese è la pagina: un `?place=` rimasto
       nell'indirizzo non si somma. */
    paese: vista.paese ? "" : first(sp.place),
    /* La voce canonica: `?cat=IT` si legge come «Information
       Technology» (lib/categorie.ts). */
    cat: canonicaCategoria(first(sp.cat)),
    date: isPeriodo(d) ? d : "",
    etype: first(sp.entity) ? first(sp.etype) : "",
    entity: first(sp.etype) ? first(sp.entity) : "",
    da: vista.intervallo ? data(first(sp.from)) : "",
    fino: vista.intervallo ? data(first(sp.before)) : "",
    page: Number.isFinite(n) && n > 0 ? n : 1,
  };
}

/** I tipi da chiedere a monte: la scelta, e senza scelta tutti quelli
 *  della vista. Sul rullo mai una lista vuota: vuota vorrebbe dire
 *  nessun filtro sul tipo, cioè l'archivio storico dentro il rullo. Dove
 *  la vista li ammette tutti invece si manda la lista vuota: undici stem
 *  in `$or` chiedono la stessa cosa di nessun filtro, e costano. */
export function tipiEffettivi(s: Pick<StatoRullo, "tipi" | "vista">): Priority[] {
  if (s.tipi.length) return s.tipi;
  return s.vista.tipi.length === PRIORITY_KEYS.length ? [] : s.vista.tipi;
}

/** Quanti restringimenti sono attivi, testo escluso: è il numero del
 *  pulsante «Filtri (n)» sul telefono, e coincide con le etichette
 *  rimovibili sotto il pannello (una per tipo, una per ogni altro
 *  filtro). */
export function filtriAttivi(s: StatoRullo): number {
  return (
    s.tipi.length +
    (s.filone ? 1 : 0) +
    (s.paese ? 1 : 0) +
    (s.cat ? 1 : 0) +
    (s.date ? 1 : 0) +
    (s.entity ? 1 : 0) +
    (s.da || s.fino ? 1 : 0)
  );
}

const fmtGiornoUtc = new Intl.DateTimeFormat("it-IT", {
  timeZone: "UTC",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** L'etichetta dell'intervallo `?from=`/`?before=`. I link di /archivio
 *  lo scrivono in UTC da capodanno a San Silvestro, e allora è «Anno
 *  2015»; un intervallo scritto a mano si legge per esteso. */
export function etichettaIntervallo(s: Pick<StatoRullo, "da" | "fino">): string {
  const a = s.da ? new Date(s.da) : null;
  const b = s.fino ? new Date(s.fino) : null;
  if (
    a &&
    b &&
    a.getUTCFullYear() === b.getUTCFullYear() &&
    s.da.startsWith(`${a.getUTCFullYear()}-01-01`) &&
    s.fino.startsWith(`${b.getUTCFullYear()}-12-31`)
  ) {
    return `Anno ${a.getUTCFullYear()}`;
  }
  if (a && b) return `Dal ${fmtGiornoUtc.format(a)} al ${fmtGiornoUtc.format(b)}`;
  if (a) return `Dal ${fmtGiornoUtc.format(a)}`;
  return b ? `Fino al ${fmtGiornoUtc.format(b)}` : "";
}

/** Il riepilogo del menu dei tipi, per nome e mai «Editoriale +9».
 *
 *  «Tutti i tipi del rullo» solo quando la scelta coincide con
 *  RULLO_PRIORITIES, cioè quando è vuota (vedi `normalizzaTipi`). Fino a
 *  tre tipi si scrivono tutti; da quattro in su i primi due e quanti altri,
 *  e l'elenco completo è nel menu. */
export function sommarioTipi(tipi: Priority[], tutti = "Tutti i tipi del rullo"): string {
  if (!tipi.length) return tutti;
  const nomi = tipi.map((t) => nomeTipo(PRIORITY_MAP[t].label));
  if (nomi.length === 1) return nomi[0]!;
  if (nomi.length === 2) return `${nomi[0]} e ${nomi[1]}`;
  /* «e altri 1» non si dice: con tre tipi si scrivono tutti. */
  if (nomi.length === 3) return `${nomi[0]}, ${nomi[1]} e ${nomi[2]}`;
  return `${nomi[0]}, ${nomi[1]} e altri ${nomi.length - 2}`;
}

/** L'indirizzo di uno stato, con qualche campo cambiato.
 *
 *  Ogni cambio che non sia la sola pagina riporta a pagina 1: un filtro
 *  nuovo è una lettura nuova, e restare a pagina 5 di un filtro che ne
 *  ha due darebbe una pagina vuota. `page=1` non si scrive, così ogni
 *  vista ha un indirizzo solo. Il modo (Sintesi / Lettura) non è un
 *  filtro: cambiarlo resta sulla stessa pagina, e ogni altro link lo
 *  porta con sé. */
export function hrefRullo(s: StatoRullo, cambi: Partial<StatoRullo> = {}): string {
  const soloPagina = Object.keys(cambi).every((k) => k === "page" || k === "modo");
  const n: StatoRullo = { ...s, ...cambi, page: soloPagina ? cambi.page ?? s.page : 1 };
  const usp = new URLSearchParams();
  if (n.q) usp.set("q", n.q);
  const tipi = normalizzaTipi(n.tipi, n.vista.tipi, n.vista.tipiEspliciti);
  if (tipi.length) usp.set("priority", tipi.join(","));
  if (n.filone) usp.set("tipo", n.filone);
  if (n.paese && !n.vista.paese) usp.set("place", n.paese);
  if (n.cat) usp.set("cat", n.cat);
  if (n.date) usp.set("date", n.date);
  if (n.entity && n.etype) {
    usp.set("etype", n.etype);
    usp.set("entity", n.entity);
  }
  if (n.vista.intervallo) {
    if (n.da) usp.set("from", n.da);
    if (n.fino) usp.set("before", n.fino);
  }
  if (n.page > 1) usp.set("page", String(n.page));
  if (n.vista.veste === "lettura" && n.modo === "sintesi") usp.set("vista", "sintesi");
  const qs = usp.toString();
  return qs ? `${n.vista.percorso}?${qs}` : n.vista.percorso;
}

/** Il link di un paese dentro la lista. Dove il paese è un filtro, lo
 *  accende; sulla scheda paese il paese è la pagina, e un altro paese
 *  (una riga che ne cita due, un riferimento) porta a /ricerca con quel
 *  paese, senza gli altri restringimenti. Il paese della scheda stessa
 *  non ha link: si è già lì (`null`). */
export function hrefPaese(s: StatoRullo, paese: string): string | null {
  if (!s.vista.paese) return hrefRullo(s, { paese });
  if (paese.toLowerCase() === s.vista.paese.nome.toLowerCase()) return null;
  return hrefRullo(leggiStato({}, VISTA_RICERCA), { paese });
}

/** Un riferimento porta ai dispacci che lo citano. Toglie gli altri
 *  restringimenti, che riguardavano la domanda di prima: da «Eni» si
 *  vogliono i dispacci su Eni, non quelli su Eni dentro «Serbia · ultimi
 *  7 giorni». Un paese della relation va su `?place`, ogni altra voce su
 *  `?etype` + `?entity`. Sulla scheda paese un altro paese porta a
 *  /ricerca (vedi `hrefPaese`), e il paese della scheda non porta da
 *  nessuna parte. Lo leggono l'elenco della scheda paese (ElencoRullo) e
 *  quello del registro di /rullo e /ricerca (components/rullo/registro). */
export function hrefRiferimento(stato: StatoRullo, f: FiltroRiferimento): string | null {
  const base: Partial<StatoRullo> = { q: "", tipi: [], cat: "", date: "", da: "", fino: "" };
  return "paese" in f
    ? hrefPaese({ ...stato, ...base, etype: "", entity: "" }, f.paese)
    : hrefRullo(stato, { ...base, paese: "", etype: f.etype, entity: f.entity });
}

/** I cambi che tolgono tutti i restringimenti e lasciano il testo: è
 *  «Azzera filtri». Il testo ha un comando suo. */
export const SENZA_FILTRI: Partial<StatoRullo> = {
  tipi: [],
  filone: "",
  paese: "",
  cat: "",
  date: "",
  etype: "",
  entity: "",
  da: "",
  fino: "",
};

/* ─────────────────────────── le righe ─────────────────────────── */

/** Il filtro su cui porta un riferimento: un paese della relation
 *  `countries` va su `place`, tutto il resto (città, aziende, persone)
 *  su `etype` + `entity`, cioè sul titolo della voce `wiki`. Misurato a
 *  monte il 28/09/2026: «Belgrado» come paese dà 0 dispacci, come voce
 *  `wiki` 16.390. Mandarlo su `place` era un link a una lista vuota. */
export type FiltroRiferimento = { paese: string } | { etype: string; entity: string };

export type GruppoRiferimenti = {
  chiave: string;
  etichetta: string;
  voci: { nome: string; filtro: FiltroRiferimento }[];
};

export type RigaRullo = {
  id: string;
  href: string;
  titolo: string;
  /** Il filone (`NewsItem.occhiello`), reso nella riga dei dati. `null` quando a monte non c'è (o non
   *  passa i controlli di `splitTitolo`): la riga allora non lo stampa. */
  occhiello: string | null;
  estratto: string;
  tipo: Priority;
  paese: string | null;
  categoria: string;
  /** Il tipo derivato dalla cronologia (notizia / caso / report), già
   *  calcolato sul server: vedi `NewsItem.tipoDerivato`. `null` quando il
   *  conteggio non c'è, e la riga allora non lo stampa. */
  tipoDerivato: TipoDerivato | null;
  /** Le tappe del filone, per «Il caso · N tappe» e i rombi
   *  (`NewsItem.tappe`). `null` quando il conteggio non c'è. */
  tappe: number | null;
  codice: string;
  /** ISO, per `<time dateTime>`. */
  iso: string;
  /** L'ora a Roma, «14:05». Non dipende da adesso: si calcola sul
   *  server e l'HTML dei due lati coincide. */
  ora: string;
  /** Il giorno a Roma, «2026-09-28», per raggruppare. */
  giorno: string;
  /** «lunedì 28 settembre 2026». */
  giornoEtichetta: string;
  riferimenti: GruppoRiferimenti[];
  /** Quante voci ci sono dietro il pulsante: la somma dei gruppi. */
  nRiferimenti: number;
};

const FUSO = "Europe/Rome";
const fmtOra = new Intl.DateTimeFormat("it-IT", {
  timeZone: FUSO,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const fmtGiornoChiave = new Intl.DateTimeFormat("en-CA", {
  timeZone: FUSO,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const fmtGiorno = new Intl.DateTimeFormat("it-IT", {
  timeZone: FUSO,
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** I gruppi del pannello: prima le imprese, poi i luoghi, poi gli altri
 *  `EntityKind` nell'ordine di ENTITY_LABELS. Un gruppo vuoto non
 *  compare. Solo entità collegate: i temi (`tags`) sono etichette messe a
 *  monte senza una rotta di filtro e non entrano né nel pannello né nel
 *  numero del pulsante. */
const ORDINE_GRUPPI: EntityKind[] = [
  "companies",
  "places",
  ...(Object.keys(ENTITY_LABELS) as EntityKind[]).filter(
    (k) => k !== "companies" && k !== "places"
  ),
];

/** «Luoghi» in ENTITY_LABELS; qui il gruppo tiene insieme paesi della
 *  relation e aree o città delle voci `wiki`, e il nome lo dice. */
const ETICHETTA_GRUPPO: Partial<Record<EntityKind, string>> = { places: "Paesi e aree" };

/** Da `StrapiCase` + `NewsItem` alla riga. Il caso grezzo serve solo a
 *  sapere quali luoghi sono paesi della relation (filtrabili per
 *  `place`) e quali voci `wiki`: `NewsItem.entities.places` li ha già
 *  fusi. */
export function rigaRullo(c: StrapiCase, d: NewsItem): RigaRullo {
  const paesiRelazione = new Set(
    (c.attributes.countries?.data ?? [])
      .map((p) => p.attributes?.title?.trim())
      .filter((t): t is string => Boolean(t))
  );
  const riferimenti: GruppoRiferimenti[] = [];
  for (const k of ORDINE_GRUPPI) {
    const nomi = d.entities[k] ?? [];
    if (!nomi.length) continue;
    riferimenti.push({
      chiave: k,
      etichetta: ETICHETTA_GRUPPO[k] ?? ENTITY_LABELS[k],
      voci: nomi.map((nome) => ({
        nome,
        filtro:
          k === "places" && paesiRelazione.has(nome)
            ? { paese: nome }
            : { etype: k, entity: nome },
      })),
    });
  }
  const t = new Date(d.time);
  return {
    id: d.id,
    href: articleHref(d),
    titolo: d.title,
    occhiello: d.occhiello ?? null,
    estratto: d.lead,
    tipo: d.priority,
    paese: [...paesiRelazione][0] ?? d.entities.places[0] ?? null,
    categoria: canonicaCategoria(d.category),
    tipoDerivato: d.tipoDerivato ?? null,
    tappe: d.tappe ?? null,
    codice: fmtDispatchId(d.id),
    iso: t.toISOString(),
    ora: fmtOra.format(t),
    giorno: fmtGiornoChiave.format(t),
    giornoEtichetta: fmtGiorno.format(t),
    riferimenti,
    nRiferimenti: riferimenti.reduce((n, g) => n + g.voci.length, 0),
  };
}

/** Le righe raggruppate per giorno, nell'ordine in cui arrivano (la
 *  lista è già per data decrescente). Sul Rullo si chiama sull'elenco
 *  accumulato e non su un blocco per volta (<RulloProgressivo>): un
 *  blocco nuovo che comincia nel giorno dell'ultima riga a schermo entra
 *  nella stessa sezione, e l'intestazione non si ripete. */
export function perGiorno(righe: RigaRullo[]): { giorno: string; etichetta: string; righe: RigaRullo[] }[] {
  const out: { giorno: string; etichetta: string; righe: RigaRullo[] }[] = [];
  for (const r of righe) {
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo.giorno === r.giorno) ultimo.righe.push(r);
    else out.push({ giorno: r.giorno, etichetta: r.giornoEtichetta, righe: [r] });
  }
  return out;
}

export const it = (n: number) => n.toLocaleString("it-IT");

/** Singolare e plurale di ogni gruppo di riferimenti, per il riassunto
 *  del comando che li apre («1 azienda · 10 luoghi e aree»). I nomi sono
 *  quelli dei gruppi del pannello (vedi ORDINE_GRUPPI). */
const NOMI_GRUPPO: Record<string, [string, string]> = {
  companies: ["azienda", "aziende"],
  places: ["luogo o area", "luoghi e aree"],
  persons: ["persona", "persone"],
  institutions: ["istituzione", "istituzioni"],
  organizations: ["organizzazione", "organizzazioni"],
  parties: ["partito", "partiti"],
  media: ["media", "media"],
  sectors: ["settore", "settori"],
  figures: ["valore", "valori"],
  tech: ["tecnologia", "tecnologie"],
};

/** Il riassunto dei riferimenti di una riga, gruppo per gruppo e nello
 *  stesso ordine del pannello. Sono le entità collegate al dispaccio a
 *  monte (la voce `wiki` e la relation `countries`): non sono le fonti e
 *  non sono le versioni, che stanno nella pagina del dispaccio. */
export function nomeGruppo(g: GruppoRiferimenti, n = g.voci.length): string {
  const [uno, molti] = NOMI_GRUPPO[g.chiave] ?? [g.etichetta.toLowerCase(), g.etichetta.toLowerCase()];
  return n === 1 ? uno : molti;
}

export function riassuntoRiferimenti(gruppi: GruppoRiferimenti[]): string {
  return gruppi
    .map((g) => `${g.voci.length} ${nomeGruppo(g)}`)
    .join(" · ");
}

/** «TEATRI DI CRISI» → «Teatri di crisi»: nei menu e nei chip i tipi si
 *  leggono come parole, non come targhe. PMI resta sigla. */
export function nomeTipo(label: string): string {
  if (label === "PMI") return label;
  return label.charAt(0) + label.slice(1).toLowerCase();
}
